import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import io from "socket.io-client";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useChainContext } from "../context/context";
import config from "../../config/config";
import { fetchAllCurrencies1 } from "@/redux/actions/FetchCoinLists";
import { logout } from "@/redux/actions/authActions";
import { setCartOpen, clearCart } from "@/redux/reducers/cartReducer";
import CartReal from "@/store/Layout/product/components/CartReal";
import PaymentSidebar from "@/shared/components/PaymentSidebar";
import { CreateOrderInternal } from "@/utils/services/user.service";
import {
  getToken,
  getUserInfo,
  logout_user,
  getNotification,
  markAllAsRead,
  getTopFiveBalance,
} from "@/utils/services/user.service";
import {
  transformVirtualWalletData,
  transformOnchainWalletData,
  transformBankData,
} from "@/store/Layout/dashboard/utils/walletDataTransformers";
import {
  NotificationMenu,
  ProfileMenu,
  NavigationMenu,
  MobileMenuComponent,
  IconButtons,
  AvatarButton,
  HeaderShell,
  Header,
  Left,
  Logo,
  Center,
  Right,
  BurgerButton,
} from "./components/walletHeader";
import { WalletSidebar } from "./components/walletHeader/WalletSidebar";

const TOP_LEVEL_SECTION_LABELS = [
  "Home",
  "Wallet",
  "OTC",
  "Trade",
  "Contract",
  "Project",
  "Marketplace",
  "More",
  "Support",
];

export default function PharmaCrowdWalletHeader() {
  const [blurIntensity, setBlurIntensity] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(3);
  const [newNotifications, setNewNotifications] = useState(1);
  const [newProducts, setNewProducts] = useState(2);
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileImage, setProfileImage] = useState("");
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationMenuOpen, setNotificationMenuOpen] = useState(false);
  const [walletSidebarOpen, setWalletSidebarOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [privateOtc, setPrivateOtc] = useState([]);
  const [publicOtc, setPublicOtc] = useState([]);
  const [myProject, setMyProject] = useState(null);
  const [otherProjects, setOtherProjects] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [walletData, setWalletData] = useState(null);
  const [bankData, setBankData] = useState(null);

  const dispatch = useDispatch();
  const { coinList: virtualWalletCoins, cartItems } = useSelector((state) => {
    return {
      coinList: state.coinList1?.data || [],
      cartItems: state.cart?.items || [],
    };
  });

  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const cartItemsCount = cartItems.reduce((acc, item) => acc + item.qty, 0);

  // Shipping and delivery charges from API (cart items carry deliveryCharges/shipping from product API)
  const cartPaymentProps = useMemo(() => {
    if (!cartItems.length) {
      return {
        orderDetail: [],
        products: [],
        shippingCost: 0,
        deliveryChargesTotal: 0,
        shippingTotal: 0,
        categoryId: null,
        symbol: "USDT",
      };
    }
    const orderDetail = cartItems.map((i) => ({ product_id: i.productId, quantity: i.qty, price: i.price, vat: i.vat }));
    const products = cartItems.map((i) => ({
      id: i.productId,
      _id: i.productId,
      name: i.name,
      title: i.name,
      price: i.price,
      vat: i.vat,
      image: i.image,
      pictures: i.image ? [i.image] : [],
      product_image: i.image,
      coverImage: i.image,
    }));
    const deliveryChargesTotal = cartItems.reduce((s, i) => s + Number(i.deliveryCharges ?? 0), 0);
    const shippingTotal = cartItems.reduce((s, i) => s + Number(i.shipping ?? 0), 0);
    const shippingCost = deliveryChargesTotal || shippingTotal;
    const snap = cartItems[0]?.productSnapshot;
    return {
      orderDetail,
      products,
      shippingCost,
      deliveryChargesTotal,
      shippingTotal,
      categoryId: snap?.category_id ?? null,
      symbol: snap?.symbol ?? "USDT",
    };
  }, [cartItems]);

  const handleCartCheckoutPay = useCallback(
    async (deliveryInfo, symbol, summary) => {
      const items = cartItems.filter((i) => i.productSnapshot);
      if (!items.length) {
        toast.error("Some cart items cannot be checked out. Remove and re-add them from the product page.");
        return;
      }
      const snap = items[0].productSnapshot;
      const product_details = items.map((i) => ({
        product_id: i.productId,
        quantity: i.qty,
        price: i.price,
        title: i.name,
        delivery_charges: Number(i.deliveryCharges ?? 0),
        vat: i.vat ?? 0,
        ...(i.variant?._id ? { detail_objectId: i.variant._id } : {}),
      }));
      const total_quantity = items.reduce((s, i) => s + i.qty, 0);
      const total_delivery_charges = items.reduce((s, i) => s + Number(i.deliveryCharges ?? 0), 0);
      await CreateOrderInternal({
        seller_wallet: snap.seller_wallet,
        agency_percentage: snap.agency_percentage ?? 0,
        buyer_approver_address: snap.buyer_approver_address ?? "",
        sale_tax: summary?.saleTax ?? 0,
        total_quantity,
        store_id: snap.store_id,
        category_id: snap.category_id,
        total_amount: summary?.totalAmount ?? 0,
        product_id: items.map((i) => i.productId),
        delivery_country: deliveryInfo.deliveryCountry,
        delivery_address: deliveryInfo.deliveryAddress,
        second_delivery_address: deliveryInfo.secondDeliveryAddress || "",
        approver_wallet_Address: snap.approver_wallet_Address ?? "",
        chain_id: snap.chain_id,
        symbol: symbol || snap.symbol || "USDT",
        total_delivery_charges,
        product_details,
        phone_number: deliveryInfo.phoneNumber,
      });
      dispatch(clearCart());
    },
    [cartItems, dispatch]
  );

  const initialSectionsState = useMemo(
    () =>
      TOP_LEVEL_SECTION_LABELS.reduce(
        (acc, label) => ({
          ...acc,
          [label]: false,
        }),
        {}
      ),
    []
  );

  const [newSections, setNewSections] = useState(initialSectionsState);

  const headerRef = useRef(null);
  const audioRef = useRef(null);
  const avatarButtonRef = useRef(null);
  const profileMenuRef = useRef(null);
  const notificationButtonRef = useRef(null);
  const notificationMenuRef = useRef(null);
  const socketInitializedRef = useRef(false);
  const history = useHistory();
  const location = useLocation();
  const {
    socket,
    setSocket,
    setGlobaltoken,
    setGlobaltokenLogo,
    reConnectSocket,
    refreshUserData,
  } = useChainContext();

  const navSections = useMemo(() => {
    const sanitizeGroups = (groups) =>
      groups
        .filter(Boolean)
        .map((group) => ({
          title: group.title ?? null,
          items: (group.items || []).filter(
            (item) => item && item.label && item.path
          ),
        }))
        .filter((group) => group.items.length);

    const otcGroups = sanitizeGroups([
      {
        title: null,
        items: [
          //   { label: 'Marketplace Overview', path: '/otcmarketplacelists' },
          privateOtc?.length
            ? { label: "Private OTC", path: "/otc/private" }
            : null,
          publicOtc?.length
            ? { label: "Public OTC", path: "/otc/public" }
            : null,
        ],
      },
    ]);

    const userRole = currentUser?.data?.user?.role;
    const baseProjectItems = [{ label: "All Projects", path: "/all-projects" }];

    if (userRole === "project" && !myProject) {
      baseProjectItems.push({
        label: "Create Project",
        path: "/CreateProject",
      });
    }

    const isWholeseller = userRole === "wholesaler";
    const isPharmacy = userRole === "pharmacy";

    const marketplaceItems = [
      // { label: 'Auction', path: '/auction' },
      { label: "Home", path: "/homeproducts" },
      { label: "Stores", path: "/store" },
    ];

    // Only show "My Stores" and "Seller Orders" for wholesellers
    // if (isWholeseller) {
    //   marketplaceItems.push(
    //     // { label: 'My Stores', path: '/storedetail' },
    //     { label: 'Seller Orders', path: '/getmyorders' }
    //   );
    // }

    // Show "My Orders" for both roles
    marketplaceItems.push({ label: "Orders", path: "/getmyorders" });

    // Show "Approvals" for wholesellers
    // if (isWholeseller) {
    //   marketplaceItems.push({ label: 'Approvals', path: '/getmyapproval' });
    // }
    marketplaceItems.push(
      // { label: 'Custom Offers', path: '/customoffers' },
      { label: "FAQs", path: "/faqs" }
    );

    return [
      { label: "Home", path: "/dashboard" },
      {
        label: "Marketplace",
        path: "/marketdashboard",
        submenus: sanitizeGroups([
          {
            title: null,
            items: marketplaceItems,
          },
        ]),
      },
      // {
      //   label: 'More',
      //   submenus: sanitizeGroups([
      //     {
      //       title: null,
      //       items: [
      //         { label: 'Currency Converter', path: '/convertor' },
      //         { label: 'Whitepaper', path: '/whitepaper' },
      //         { label: 'Token', path: '/token' },
      //         { label: 'Roadmap', path: '/roadmap' },
      //       ],
      //     },
      //   ]),
      // },
      {
        label: "Contract",
        submenus: sanitizeGroups([
          {
            title: null,
            items: [
              { label: "Create Safebox", path: "/new-safebox" },
              { label: "Safebox List", path: "/contractsLists" },
            ],
          },
        ]),
      },
      {
        label: "Support",
        submenus: sanitizeGroups([
          {
            title: null,
            items: [
              { label: "Customer Service", path: "/support" },
              { label: "Live Chat", path: "/livechat" },
            ],
          },
        ]),
      },
    ];
  }, [privateOtc, publicOtc, myProject, otherProjects, currentUser]);

  useEffect(() => {
    setNewSections((prev) => {
      const hasMissing = navSections.some(
        (section) => typeof prev[section.label] === "undefined"
      );
      if (!hasMissing) {
        return prev;
      }
      const next = { ...prev };
      navSections.forEach((section) => {
        if (typeof next[section.label] === "undefined") {
          next[section.label] = false;
        }
      });
      return next;
    });
  }, [navSections]);

  useEffect(() => {
    const handleScroll = () => {
      const scroll = Math.min(window.scrollY / 300, 1);
      setBlurIntensity(scroll);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    let isMounted = true;

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setUnreadMessages((prev) => {
        if (prev > 0) {
          return prev - 1;
        }
        if (Math.random() > 0.7) {
          audioRef.current?.play().catch(() => {});
          return Math.floor(Math.random() * 4) + 1;
        }
        return prev;
      });

      setNewNotifications((prev) => {
        if (prev > 0) {
          return prev - 1;
        }
        if (Math.random() > 0.8) {
          audioRef.current?.play().catch(() => {});
          return Math.floor(Math.random() * 3) + 1;
        }
        return prev;
      });

      setNewProducts((prev) => {
        if (prev > 0) {
          return prev - 1;
        }
        if (Math.random() > 0.85) {
          audioRef.current?.play().catch(() => {});
          return Math.floor(Math.random() * 2) + 1;
        }
        return prev;
      });
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setNewSections((prev) => {
        const updated = { ...prev };

        ["OTC", "Trade", "Project", "Marketplace"].forEach((key) => {
          if (Math.random() > 0.8) {
            updated[key] = true;
            audioRef.current?.play().catch(() => {});
          }
        });

        return updated;
      });
    }, 9000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setProfileMenuOpen(false);
    setNotificationMenuOpen(false);
    setOpenSubmenu(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!profileMenuOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      const target = event.target;
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(target) &&
        avatarButtonRef.current &&
        !avatarButtonRef.current.contains(target)
      ) {
        setProfileMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setProfileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [profileMenuOpen]);

  useEffect(() => {
    if (!notificationMenuOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      const target = event.target;
      if (
        notificationMenuRef.current &&
        !notificationMenuRef.current.contains(target) &&
        notificationButtonRef.current &&
        !notificationButtonRef.current.contains(target)
      ) {
        setNotificationMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setNotificationMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [notificationMenuOpen]);

  useEffect(() => {
    const cachedUser = JSON.parse(localStorage.getItem("user"));
    setCurrentUser(cachedUser);
    const cachedImage =
      cachedUser?.data?.user?.image ||
      cachedUser?.data?.user?.profileImage ||
      cachedUser?.data?.user?.avatar;
    if (cachedImage) {
      setProfileImage(cachedImage);
    }

    let isMounted = true;
    const fetchProfile = async () => {
      try {
        const res = await getUserInfo();
        const remoteImage = res?.data?.User?.image;
        if (remoteImage && isMounted) {
          setProfileImage(remoteImage);
        }
      } catch (error) {
        console.error("Error fetching profile image:", error);
      }
    };

    fetchProfile();

    return () => {
      isMounted = false;
    };
  }, [refreshUserData]);

  useEffect(() => {
    let isMounted = true;
    const fetchNotifications = async () => {
      try {
        const payload = {
          type: "user",
          limit: 10,
          page_number: 1,
        };
        const response = await getNotification(payload);
        if (isMounted && response?.data?.Notifications) {
          setNotifications(response.data.Notifications);
          const unreadCount = response.data.Notifications.filter(
            (notification) => !notification.is_read
          ).length;
          setNewNotifications(unreadCount);
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };

    fetchNotifications();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!openSubmenu) {
      return undefined;
    }

    const handleDocumentClick = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setOpenSubmenu(null);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
    };
  }, [openSubmenu]);

  useEffect(() => {
    const status = JSON.parse(localStorage.getItem("user"));
    console.log("🚀 [SOCKET] useEffect triggered", {
      route: location.pathname,
      hasSocket: !!socket,
      socketConnected: socket?.connected,
      reConnectSocket,
      socketInitialized: socketInitializedRef.current,
    });

    if (!status?.data?.user) {
      console.log("❌ [SOCKET] User not logged in, disconnecting socket");
      if (socket) {
        socket.disconnect();
      }
      setSocket(null);
      socketInitializedRef.current = false;
      return () => {};
    }

    // If socket already exists and is connected, ensure listeners are set up
    if (socket && socket.connected) {
      console.log("✅ [SOCKET] Socket already connected, ensuring listeners", {
        route: location.pathname,
      });

      // Remove existing listeners to avoid duplicates
      socket.off("connect");
      socket.off("message");

      const handleConnect = () => {
        const userId = JSON.parse(localStorage.getItem("user"))?.data?.user
          ?.user_id;
        console.log("🚀 ~ PharmaCrowdWalletHeader ~ userId:----1", userId);
        if (userId) {
          console.log("🚀 ~ PharmaCrowdWalletHeader ~ userId:", userId);
          socket.emit("connected", { subject: "testing", user_id: userId });
        }
      };

      const handleMessage = (newMessage) => {
        toast.info(`${newMessage?.user?.name} sent you a message`);
      };

      socket.on("connect", handleConnect);
      socket.on("message", handleMessage);

      // Emit connected event immediately if already connected
      const userId = JSON.parse(localStorage.getItem("user"))?.data?.user
        ?.user_id;
      if (userId) {
        console.log("🚀 ~ PharmaCrowdWalletHeader ~ userId:", userId);
        socket.emit("connected", { subject: "testing", user_id: userId });
      }

      return () => {
        socket.off("connect", handleConnect);
        socket.off("message", handleMessage);
      };
    }

    // If reConnectSocket is false and socket exists but not connected, try to reconnect
    if (reConnectSocket === false && socket && !socket.connected) {
      console.log(
        "🔄 [SOCKET] reConnectSocket is false, reconnecting existing socket"
      );
      socket.connect();
      return () => {};
    }

    // Only create new socket if reConnectSocket is false and socket doesn't exist or not initialized
    if (
      reConnectSocket === false &&
      (!socket || !socketInitializedRef.current)
    ) {
      console.log("🔌 [SOCKET] Creating new socket instance", {
        route: location.pathname,
        hasSocket: !!socket,
        initialized: socketInitializedRef.current,
      });

      // Disconnect existing socket if it exists
      if (socket) {
        socket.disconnect();
        setSocket(null);
      }

      socketInitializedRef.current = true;

      const socketInstance = io(config.baseURL, {
        transports: ["websocket", "polling"],
        withCredentials: true,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: Infinity,
      });

      console.log(
        "🚀 ~ PharmaCrowdWalletHeader ~ socketInstance:",
        socketInstance
      );
      console.log(
        "🚀 ~ PharmaCrowdWalletHeader ~ socketInstance.connected:",
        socketInstance.connected
      );

      // Always set up event listeners - socket will connect automatically
      socketInstance.on("connect", () => {
        console.log("✅ [SOCKET] Socket connected successfully", {
          route: location.pathname,
          socketId: socketInstance.id,
        });
        const userId = JSON.parse(localStorage.getItem("user"))?.data?.user
          ?.user_id;
        console.log("🚀 ~ PharmaCrowdWalletHeader ~ userId:----1", userId);
        if (userId) {
          console.log("🚀 ~ PharmaCrowdWalletHeader ~ userId:", userId);
          socketInstance.emit("connected", {
            subject: "testing",
            user_id: userId,
          });
        }
      });

      socketInstance.on("disconnect", (reason) => {
        console.log("⚠️ [SOCKET] Disconnected:", reason, {
          route: location.pathname,
        });
      });

      socketInstance.on("reconnect", (attemptNumber) => {
        console.log(
          "✅ [SOCKET] Reconnected after",
          attemptNumber,
          "attempts",
          { route: location.pathname }
        );
        const userId = JSON.parse(localStorage.getItem("user"))?.data?.user
          ?.user_id;
        if (userId) {
          socketInstance.emit("connected", {
            subject: "testing",
            user_id: userId,
          });
        }
      });

      socketInstance.on("message", (newMessage) => {
        console.log("📨 [SOCKET] Received message:", newMessage);
        toast.info(`${newMessage?.user?.name} sent you a message`);
      });

      setSocket(socketInstance);

      // Cleanup function: Only disconnect on logout, not on route changes
      return () => {
        const userStatus = JSON.parse(localStorage.getItem("user"));
        if (!userStatus?.data?.user) {
          console.log("🧹 [SOCKET] User logged out, cleaning up socket");
          socketInitializedRef.current = false;
          socketInstance.off("connect");
          socketInstance.off("disconnect");
          socketInstance.off("reconnect");
          socketInstance.off("message");
          socketInstance.disconnect();
          setSocket(null);
        } else {
          console.log("🔄 [SOCKET] Route changed, keeping socket connected", {
            route: location.pathname,
          });
        }
      };
    }

    return () => {};
  }, [setSocket, reConnectSocket, socket, location.pathname]);

  useEffect(() => {
    let isMounted = true;

    const fetchTokens = async () => {
      try {
        const res = await getToken({ chain_id: "" });
        if (!isMounted) {
          return;
        }
        const transformedTokens =
          res?.data?.user?.map((token) => ({
            label: token?.symbol,
            value: token?.symbol,
          })) || [];
        const transformedTokensLogo =
          res?.data?.user?.map((token) => ({
            symbol: token?.symbol,
            url: token?.logo,
          })) || [];
        setGlobaltoken(transformedTokens);
        setGlobaltokenLogo(transformedTokensLogo);
      } catch (error) {
        console.error("Error fetching tokens:", error);
      }
    };

    fetchTokens();

    return () => {
      isMounted = false;
    };
  }, [setGlobaltoken, setGlobaltokenLogo]);

  useEffect(() => {
    dispatch(fetchAllCurrencies1()).catch(() => {});
  }, [dispatch]);

  // Fetch wallet balances for WalletSidebar
  // useEffect(() => {
  //   const fetchWalletBalances = async () => {
  //     try {
  //       // Fetch onchain wallet data (also contains bank data if available)
  //       const balanceRes = await getTopFiveBalance();
  //       setWalletData(balanceRes?.data);

  //       // Bank data might be in walletData or might need separate fetch
  //       // For now, we'll extract it from walletData if available
  //       if (balanceRes?.data?.bank) {
  //         setBankData(balanceRes.data.bank);
  //       } else if (balanceRes?.data?.striga) {
  //         setBankData(balanceRes.data.striga);
  //       }
  //     } catch (error) {
  //       console.error("Error fetching wallet balances:", error);
  //     }
  //   };

  //   fetchWalletBalances();
  // }, []);

  // Calculate wallet totals for WalletSidebar
  const walletBalances = useMemo(() => {
    // Transform virtual wallet data
    const transformVirtualWalletDataMemo =
      transformVirtualWalletData(virtualWalletCoins);
    const virtualTotal = (() => {
      const storedTotal = localStorage.getItem("internalCoins");
      if (storedTotal) {
        return parseFloat(storedTotal.replace(/"/g, "")) || 0;
      }
      return transformVirtualWalletDataMemo.reduce(
        (acc, a) => acc + a.balance,
        0
      );
    })();

    // Transform onchain wallet data
    const onchainData = walletData?.onchain?.[0]?.topBalances || [];
    const transformOnchainWalletDataMemo =
      transformOnchainWalletData(onchainData);
    const onchainTotal = transformOnchainWalletDataMemo.reduce(
      (acc, a) => acc + a.balance,
      0
    );

    // Transform bank wallet data
    const bankItems = transformBankData(bankData);
    const bankTotal = (() => {
      if (bankData?.totalBalance) {
        return parseFloat(bankData.totalBalance) || 0;
      }
      return bankItems.reduce((acc, a) => acc + a.balance, 0);
    })();

    // Format for WalletSidebar
    return [
      {
        kind: "virtual",
        label: "Virtual Wallet",
        totalFiat: virtualTotal,
        fiatCcy: "USD",
        tokens: transformVirtualWalletDataMemo.map((item) => ({
          symbol: item.symbol,
          value: item.qty,
        })),
      },
      {
        kind: "onchain",
        label: "On-chain Wallet",
        totalFiat: onchainTotal,
        fiatCcy: "USD",
        tokens: transformOnchainWalletDataMemo.map((item) => ({
          symbol: item.symbol,
          value: item.qty,
        })),
      },
      {
        kind: "bank",
        label: "Bank Wallet",
        totalFiat: bankTotal,
        fiatCcy: "USD",
        tokens: bankItems.map((item) => ({
          symbol: item.currency,
          value: item.qty,
        })),
      },
    ];
  }, [virtualWalletCoins, walletData, bankData]);

  const getNormalizedPath = (path) => (path ? path.split("?")[0] : "");

  const isSectionActive = (section) => {
    const currentPath = location.pathname;
    if (
      section.path &&
      currentPath.startsWith(getNormalizedPath(section.path))
    ) {
      return true;
    }
    if (Array.isArray(section.submenus)) {
      return section.submenus.some((group) =>
        group.items.some(
          (item) =>
            item.path && currentPath.startsWith(getNormalizedPath(item.path))
        )
      );
    }
    return false;
  };

  const handleSectionClick = (section) => {
    const hasSubmenu =
      Array.isArray(section.submenus) && section.submenus.length > 0;
    setNewSections((prev) => ({
      ...prev,
      [section.label]: false,
    }));
    if (hasSubmenu) {
      setOpenSubmenu((prev) => (prev === section.label ? null : section.label));
      return;
    }
    setOpenSubmenu(null);
    if (section.path) {
      history.push(section.path);
      if (menuOpen) {
        setMenuOpen(false);
      }
    }
  };

  const handleSubItemClick = (item, parentLabel) => {
    if (!item?.path) {
      return;
    }
    setOpenSubmenu(null);
    setMenuOpen(false);
    setNewSections((prev) => ({
      ...prev,
      [parentLabel]: false,
    }));
    history.push(item.path);
  };

  const toggleMobileMenu = () => {
    setOpenSubmenu(null);
    setMenuOpen((prev) => !prev);
  };

  const fetchNotifications = async () => {
    try {
      const payload = {
        type: "user",
        limit: 10,
        page_number: 1,
      };
      const response = await getNotification(payload);
      if (response?.data?.Notifications) {
        setNotifications(response.data.Notifications);
        const unreadCount = response.data.Notifications.filter(
          (notification) => !notification.is_read
        ).length;
        setNewNotifications(unreadCount);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const payload = {
        type: "user",
      };
      const response = await markAllAsRead(payload);
      if (response?.data?.success === true) {
        fetchNotifications();
      }
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  const handleIconClick = (type) => {
    setOpenSubmenu(null);
    setMenuOpen(false);
    if (type === "subscription") {
      history.push("/subscription");
    }
    if (type === "wallet") {
      setWalletSidebarOpen(true);
    }
    if (type === "chat") {
      history.push("/livechat");
    }
    if (type === "notif") {
      const willOpen = !notificationMenuOpen;
      setNotificationMenuOpen((prev) => !prev);
      if (willOpen) {
        fetchNotifications();
      }
    }
    if (type === "cart") {
      setNewProducts(0);
      dispatch(setCartOpen(true));
    }
  };

  const toggleProfileMenu = () => {
    setProfileMenuOpen((prev) => !prev);
  };

  const navigateTo = (path) => {
    setProfileMenuOpen(false);
    history.push(path);
  };

  const handleLogout = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      dispatch(logout());
      if (user?.data?.user?.user_id) {
        await logout_user({ user_id: user.data.user.user_id });
      }
    } catch (error) {
      console.error("Error logging out:", error);
    } finally {
      setProfileMenuOpen(false);
      history.push("/");
    }
  };

  const profileMenuItems = [
    { label: "My Profile", icon: "user", action: () => navigateTo("/profile") },
    {
      label: "Calendar",
      icon: "calendar-full",
      action: () => navigateTo("/default_pages/calendar"),
    },
    { label: "Tasks", icon: "list", action: () => navigateTo("/todo") },
    { label: "Timeline", icon: "list", action: () => navigateTo("/timeline") },
    // { label: 'Sub Accounts', icon: 'inbox', action: () => navigateTo('/subaccounts') },
    { type: "divider" },
    {
      label: "Account Settings",
      icon: "cog",
      action: () => navigateTo("/profile"),
    },
    { label: "Log Out", icon: "exit", action: handleLogout },
  ];

  return (
    <HeaderShell>
      <Header blurIntensity={blurIntensity} ref={headerRef}>
        <Left>
          <Link to="/dashboard">
            <Logo>PharmaCrowd</Logo>
          </Link>
        </Left>
        <BurgerButton
          type="button"
          aria-label="Toggle navigation"
          aria-expanded={menuOpen}
          onClick={toggleMobileMenu}
        >
          <Menu size={18} />
        </BurgerButton>
        <Center>
          <NavigationMenu
            navSections={navSections}
            openSubmenu={openSubmenu}
            isSectionActive={isSectionActive}
            handleSectionClick={handleSectionClick}
            handleSubItemClick={handleSubItemClick}
          />
        </Center>
        <Right>
          <IconButtons
            unreadMessages={unreadMessages}
            newNotifications={newNotifications}
            newProducts={cartItemsCount}
            notificationMenuOpen={notificationMenuOpen}
            notificationButtonRef={notificationButtonRef}
            onIconClick={handleIconClick}
          />
          {notificationMenuOpen && (
            <NotificationMenu
              notifications={notifications}
              onMarkAllAsRead={handleMarkAllAsRead}
              menuRef={notificationMenuRef}
            />
          )}
          <AvatarButton
            profileImage={profileImage}
            avatarButtonRef={avatarButtonRef}
            profileMenuOpen={profileMenuOpen}
            toggleProfileMenu={toggleProfileMenu}
          />
          {profileMenuOpen && (
            <ProfileMenu
              menuItems={profileMenuItems}
              menuRef={profileMenuRef}
            />
          )}
        </Right>
        {menuOpen && (
          <MobileMenuComponent
            navSections={navSections}
            openSubmenu={openSubmenu}
            isSectionActive={isSectionActive}
            handleSectionClick={handleSectionClick}
            handleSubItemClick={handleSubItemClick}
            newSections={newSections}
            onIconClick={handleIconClick}
            unreadMessages={unreadMessages}
          />
        )}
        <audio
          ref={audioRef}
          src="data:audio/mp3;base64,//uQZAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAACcQCA/////wAAABJHQVMAAAABAAAADwAAABJtbHMAAAAAAAAAAAA="
          preload="auto"
        />
        <WalletSidebar
          open={walletSidebarOpen}
          onOpenChange={setWalletSidebarOpen}
          balances={walletBalances}
          onRefresh={() => {
            dispatch(fetchAllCurrencies1()).catch(() => {});
          }}
        />
        <CartReal
          onCheckout={() => {
            dispatch(setCartOpen(false));
            setCheckoutOpen(true);
          }}
        />
        {checkoutOpen && cartItems.length > 0 && (
          <PaymentSidebar
            open={checkoutOpen}
            onOpenChange={setCheckoutOpen}
            mode="buy"
            title="Checkout"
            subtitle="Complete your purchase"
            productSymbol={cartPaymentProps.symbol}
            orderDetail={cartPaymentProps.orderDetail}
            products={cartPaymentProps.products}
            shippingCost={cartPaymentProps.shippingCost}
            categoryId={cartPaymentProps.categoryId}
            onBuyPay={handleCartCheckoutPay}
            onPurchaseSuccess={() => setCheckoutOpen(false)}
          />
        )}
      </Header>
    </HeaderShell>
  );
}
